import { useRef, useState, useEffect } from 'react';

const GUARD_STATE_KEY = '__hasGuardEntry';

/**
 * useNavigationGuard is a hook that allows you to block navigation
 * to previous pages in the browser history. It also provides
 * methods to confirm, cancel or release the block.
 *
 * @param {boolean} shouldBlock - whether to block navigation
 * @returns {object} - an object containing cancel, confirm, release
 * and isBlocked properties
 */
export const useNavigationGuard = (shouldBlock: boolean) => {
  const shouldBlockRef = useRef(shouldBlock);
  const hasExtraEntryRef = useRef(false);
  const isReleasedRef = useRef(false);

  shouldBlockRef.current = shouldBlock && !isReleasedRef.current;

  const [isBlocked, setIsBlocked] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (shouldBlock && !hasExtraEntryRef.current) {
      if (!window.history.state?.[GUARD_STATE_KEY]) {
        window.history.pushState(
          { ...window.history.state, [GUARD_STATE_KEY]: true },
          '',
          window.location.href
        );
      }
      hasExtraEntryRef.current = true;
    }
  }, [shouldBlock]);

  useEffect(() => {
    const handlePopState = () => {
      if (isReleasedRef.current) {
        hasExtraEntryRef.current = false;
        return;
      }
      if (shouldBlockRef.current) {
        window.history.pushState(
          window.history.state,
          '',
          window.location.href
        );
        pendingActionRef.current = () => window.history.go(-2);
        setIsBlocked(true);
      } else {
        hasExtraEntryRef.current = false;
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const guardedNavigate = (action: () => void) => {
    if (shouldBlockRef.current) {
      pendingActionRef.current = action;
      setIsBlocked(true);
    } else {
      action();
    }
  };

  const release = (): boolean => {
    isReleasedRef.current = true;
    if (hasExtraEntryRef.current) {
      hasExtraEntryRef.current = false;
      window.history.go(-2);
      return true;
    }
    return false;
  };

  const confirm = () => {
    setIsBlocked(false);
    pendingActionRef.current?.();
    pendingActionRef.current = null;
  };

  const cancel = () => {
    setIsBlocked(false);
    pendingActionRef.current = null;
  };

  return { cancel, confirm, release, isBlocked, guardedNavigate };
};
